import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Flower2, Heart, LockKeyhole, Mail, UserRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import "./AdminLogin.css";

// Free-to-use Indian wedding photography by Sabesh Photography LTD on Unsplash.
// Keep the wedding visuals independent of the authentication API.
const WEDDING_PHOTOS = {
  signin: "https://unsplash.com/photos/OzyvCE9a60M/download?force=true&w=1400&q=85",
  signup: "https://unsplash.com/photos/3KDfgC69xBE/download?force=true&w=1400&q=85",
};

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" role="img" aria-label="Google">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.3 5.48-4.87 7.18l7.73 6C44.31 38.03 46.98 31.88 46.98 24.55z" />
      <path fill="#FBBC05" d="M10.53 28.59A14.41 14.41 0 0 1 9.75 24c0-1.59.27-3.13.76-4.59l-7.98-6.2A23.91 23.91 0 0 0 0 24c0 3.87.93 7.52 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.9-5.8l-7.73-6C30.03 37.63 27.28 38.5 24 38.5c-6.26 0-11.57-4.22-13.47-9.91l-7.97 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function WeddingVisual({ mode, reducedMotion }) {
  const signup = mode === "signup";
  return (
    <div className="rv-auth-visual" aria-label="Indian wedding photography">
      <AnimatePresence initial={false} mode="sync">
        <motion.img
          key={mode}
          className="rv-auth-photo"
          src={WEDDING_PHOTOS[mode]}
          alt={signup ? "Indian newlyweds sharing a joyful moment" : "Indian bride and groom smiling together at their wedding"}
          initial={reducedMotion ? false : { opacity: 0, scale: 1.035 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : .6 }}
          onError={(event) => { event.currentTarget.style.display = "none"; }}
        />
      </AnimatePresence>
      <div className="rv-auth-photo-shade" />
      <div className="rv-auth-photo-grain" />
      <div className="rv-auth-visual-top" aria-hidden="true">
        <span className="rv-auth-visual-top-line" />
        <Flower2 size={27} strokeWidth={1.35} />
        <span className="rv-auth-visual-top-line" />
      </div>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={mode}
          className="rv-auth-visual-copy"
          initial={reducedMotion ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: reducedMotion ? 0 : .38 }}
        >
          <div className="rv-auth-visual-eyebrow">The art of celebrating love</div>
          <h2>{signup ? "Your forever begins here." : "The beautiful moments start here."}</h2>
          <p>{signup
            ? "Create invitations as memorable as your wedding day."
            : "Your wedding, your people, your cherished celebrations — all in one place."}</p>
        </motion.div>
      </AnimatePresence>
      <div className="rv-auth-visual-credit">
        Photography: <a href="https://unsplash.com/@sabesh" target="_blank" rel="noopener noreferrer">Sabesh Photography LTD / Unsplash</a>
      </div>
    </div>
  );
}

export default function AdminLogin() {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [weddingTitle, setWeddingTitle] = useState("Our Wedding");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const reducedMotion = useReducedMotion();
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const signup = mode === "signup";

  const selectMode = (nextMode) => {
    if (loading || nextMode === mode) return;
    setMode(nextMode);
    setShowPassword(false);
    setError("");
    setMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      if (signup) {
        const result = await register(email, password, name, weddingTitle);
        if (result.requires_confirmation) {
          setPassword("");
          setShowPassword(false);
          setMode("signin");
          setMessage("Please check your email to confirm your account, then sign in.");
          return;
        }
      } else {
        await login(email, password);
      }
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((item) => item.msg || "").join(" ") :
        signup ? "Unable to create your account. Please try again." :
        "Unable to sign in. Please check your details and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setMessage("");
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch {
      setError("Google sign-in is unavailable right now. Please use your email and password.");
      setLoading(false);
    }
  };

  return (
    <main className="rv-auth-page" data-testid="admin-login-page">
      <section className="rv-auth-shell" aria-label="Royal Vivah account access">
        <aside className="rv-auth-rail">
          <div className="rv-auth-mark" aria-label="Royal Vivah">
            <span className="rv-auth-mark-icon"><Heart size={24} strokeWidth={1.7} /></span>
            <span className="rv-auth-mark-name">Royal<br />Vivah</span>
          </div>
          <nav className="rv-auth-tabs" aria-label="Account access">
            <button className="rv-auth-tab" type="button" aria-pressed={!signup} onClick={() => selectMode("signin")} disabled={loading}>
              <LockKeyhole size={22} strokeWidth={1.8} />
              <span>Sign in</span>
            </button>
            <button className="rv-auth-tab" type="button" aria-pressed={signup} onClick={() => selectMode("signup")} disabled={loading}>
              <UserRound size={22} strokeWidth={1.8} />
              <span>Sign up</span>
            </button>
          </nav>
          <div className="rv-auth-rail-detail" aria-hidden="true"><Flower2 size={24} strokeWidth={1.2} /></div>
        </aside>

        <WeddingVisual mode={mode} reducedMotion={reducedMotion} />

        <div className="rv-auth-content">
          <div className="rv-auth-form-wrap">
            <AnimatePresence initial={false} mode="wait">
              <motion.form
                key={mode}
                onSubmit={handleSubmit}
                initial={reducedMotion ? false : { opacity: 0, x: signup ? 15 : -15 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: signup ? -15 : 15 }}
                transition={{ duration: reducedMotion ? 0 : .25 }}
              >
                <div className="rv-auth-overline">Welcome to Royal Vivah</div>
                <h1 className="rv-auth-form-heading">{signup ? "Let's celebrate love." : "Welcome back."}</h1>
                <p className="rv-auth-form-subtitle">
                  {signup
                    ? "A beautiful beginning deserves a beautiful invitation."
                    : "Sign in to continue planning your special day."}
                </p>

                <div className="rv-auth-fields">
                  {signup && (
                    <>
                      <div className="rv-auth-field">
                        <label htmlFor="rv-auth-name">Your name</label>
                        <div className="rv-auth-input-wrap">
                          <UserRound size={17} aria-hidden="true" />
                          <input id="rv-auth-name" data-testid="register-name-input" className="rv-auth-input" value={name}
                            onChange={(event) => setName(event.target.value)} autoComplete="name"
                            maxLength={120} required placeholder="Your name" />
                        </div>
                      </div>
                      <div className="rv-auth-field">
                        <label htmlFor="rv-auth-wedding">Wedding name</label>
                        <div className="rv-auth-input-wrap">
                          <Heart size={17} aria-hidden="true" />
                          <input id="rv-auth-wedding" className="rv-auth-input" value={weddingTitle}
                            onChange={(event) => setWeddingTitle(event.target.value)}
                            maxLength={160} required placeholder="Our Wedding" />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="rv-auth-field">
                    <label htmlFor="rv-auth-email">Email address</label>
                    <div className="rv-auth-input-wrap">
                      <Mail size={17} aria-hidden="true" />
                      <input id="rv-auth-email" data-testid={signup ? "register-email-input" : "login-email-input"}
                        className="rv-auth-input" type="email" value={email}
                        onChange={(event) => setEmail(event.target.value)} autoComplete="email"
                        required placeholder="you@example.com" />
                    </div>
                  </div>
                  <div className="rv-auth-field">
                    <label htmlFor="rv-auth-password">Password</label>
                    <div className="rv-auth-input-wrap">
                      <LockKeyhole size={17} aria-hidden="true" />
                      <input id="rv-auth-password" data-testid={signup ? "register-password-input" : "login-password-input"}
                        className="rv-auth-input" type={showPassword ? "text" : "password"}
                        value={password} onChange={(event) => setPassword(event.target.value)}
                        autoComplete={signup ? "new-password" : "current-password"}
                        minLength={signup ? 12 : undefined} required
                        placeholder={signup ? "At least 12 characters" : "Enter your password"} />
                      <button className="rv-auth-reveal" type="button" onClick={() => setShowPassword((previous) => !previous)}
                        aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {signup && <span className="rv-auth-note">Use at least 12 characters for your account.</span>}
                  </div>
                </div>

                {error && <div className="rv-auth-alert rv-auth-alert-error" role="alert" data-testid="login-error-message">{error}</div>}
                {message && <div className="rv-auth-alert rv-auth-alert-success" role="status">{message}</div>}
                <button className="rv-auth-submit" data-testid={signup ? "register-submit-button" : "login-submit-button"}
                  type="submit" disabled={loading}>
                  {loading ? "Please wait..." : signup ? "Create your account" : "Sign in"}
                  {!loading && <ArrowRight size={17} strokeWidth={1.8} aria-hidden="true" />}
                </button>
              </motion.form>
            </AnimatePresence>

            <div className="rv-auth-divider">or continue with</div>
            <button className="rv-auth-google" type="button" onClick={handleGoogle} disabled={loading}>
              <GoogleMark /> Continue with Google
            </button>
            <p className="rv-auth-switch">
              {signup ? "Already have an account? " : "New to Royal Vivah? "}
              <button type="button" onClick={() => selectMode(signup ? "signin" : "signup")} disabled={loading}>
                {signup ? "Sign in" : "Create an account"}
              </button>
            </p>
            <p className="rv-auth-bottom">Thoughtfully made for your once-in-a-lifetime celebration.</p>
          </div>
        </div>
      </section>
    </main>
  );
}